import React, { useCallback, useEffect, useRef, useState } from "react";
import { Empty, message, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { addEditNote } from "../../../../store/chats/thunks";
import { useNotes } from "../../../../hooks/useNotes";
import { getFullName } from "../../../../utils/shared";
import TextArea from "antd/es/input/TextArea";
import { scrollToBottom } from "../../../../utils/uiElementScroll";
import dayjs from "../../../../utils/dayjs";
import CustomCollapse from "../../../../components/generic/CustomDetails";
import Modal from "../../../../components/generic/Modal";
import { selectUser } from "../../../../store/user/selectors";

const Notes = () => {
  const [submitting, setSubmitting] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [selectedNote, setSelectedNote] = useState(null);

  const user = useSelector(selectUser);
  const notesBodyRef = useRef(null);

  const dispatch = useDispatch();

  const { notes, loading, selectedTenantId, selectedChatId } = useNotes();

  const onAddNote = useCallback(
    async (e) => {
      try {
        const finalNote = e?.target?.value || newNote;
        setSubmitting(true);
        if (!finalNote?.trim()) return message.error("Note content required");
        await dispatch(
          addEditNote({
            conversation_id: selectedChatId,
            note_content: finalNote,
            tenant_id: selectedTenantId,
            conversation_note_id: selectedNote?.id || null,
          }),
        );
        setSelectedNote(null);
        scrollToBottom(notesBodyRef);
        setNewNote("");
      } finally {
        setSubmitting(false);
      }
    },
    [selectedChatId, selectedTenantId, newNote, selectedNote],
  );

  useEffect(() => {
    if (selectedChatId && !loading) scrollToBottom(notesBodyRef);
  }, [loading, selectedChatId]);

  useEffect(() => {
    setNewNote(selectedNote?.note_content || "");
  }, [selectedNote]);

  return (
    <>
      <div className="flex-col notes-body">
        <CustomCollapse
          className="custom-collapse productivity-padding notes-collapse"
          title="Notes"
          //@ts-ignore
          headerRight={
            <span
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote({});
              }}
            >
              <PlusOutlined />
            </span>
          }
          open
        >
          <div className="notes-content" ref={notesBodyRef}>
            {loading ? (
              <div
                className="flex justify-center h-100 w-100 align-center"
                style={{
                  marginTop: "10px",
                }}
              >
                <Spin />
              </div>
            ) : notes?.length ? (
              notes.map((note) => (
                <div
                  key={note?.id}
                  className="note-item cursor-pointer"
                  onClick={() => {
                    if (
                      note?.advisor?.id &&
                      note?.advisor?.id === user?.user_id
                    )
                      setSelectedNote(note);
                    else message.error("You can only edit your own note");
                  }}
                >
                  <div className="note-header">
                    <span className="note-author">
                      {getFullName(note?.advisor)}
                    </span>
                    <span className="note-time">
                      {dayjs(note?.created_at).format("DD-MM-YYYY, hh:mm A")}
                    </span>
                  </div>
                  <div key={note.id}>
                    <div className="note-content">{note.note_content}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex justify-center h-100 w-100 align-center">
                <Empty
                  description="No Notes Found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </div>
        </CustomCollapse>
        <div className="notes-footer">
          <TextArea
            placeholder="Type a note specific to this ticket..."
            value={newNote}
            disabled={submitting}
            autoSize={{ minRows: 2, maxRows: 2 }}
            onChange={(e) => setNewNote(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                onAddNote(e);
              }
            }}
          />
        </div>
      </div>
      <Modal
        isProcessing={submitting}
        title={selectedNote?.id ? "Edit Selected Note" : "Add New Note"}
        onCancel={() => setSelectedNote(null)}
        width={500}
        open={!!selectedNote}
        onOk={onAddNote}
      >
        <div className="add-note-section">
          <TextArea
            placeholder="Type a note specific to this ticket..."
            value={newNote}
            disabled={submitting}
            autoSize={{ minRows: 3, maxRows: 10 }}
            onChange={(e) => setNewNote(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default Notes;
